import dayjs from 'dayjs'
import { useMemo } from 'react'

import { PlatePage_CulturePlate } from '~/api/backend/pages/platePageAPI'
import LineGraph from '~/components/LineGraph'
import { generateWellColor } from '~/pages/Monitor/utils/MonitorGraphColors'

export interface PlateCellConcentrationGraphSideBarProps {
  plate: PlatePage_CulturePlate
  highlightedWell?: string
}

interface CellConcentrationObservation {
  timestamp: number // Unix timestamp in seconds
  concentration: number // cells per mL
}

export function PlateCellConcentrationGraphSideBar({
  plate,
  highlightedWell,
}: PlateCellConcentrationGraphSideBarProps) {
  const { graphData, legendData, allTimestamps, minTime, maxTime } = useMemo(() => {
    const wellConcentrationData: { [well: string]: CellConcentrationObservation[] } = {}
    const wellColors: { [well: string]: string } = {}
    let wellIndex = 0

    // Extract cell concentration data for each well from observation history
    plate.plateObservationHistory.forEach(observation => {
      if ((observation as any).cell_concentrations) {
        const timestamp = dayjs(observation.timestamp).unix()
        // line break
        ;(observation as any).cell_concentrations.forEach((concentration: any) => {
          const well = concentration.well
          if (!wellConcentrationData[well]) {
            wellConcentrationData[well] = []
            wellColors[well] = generateWellColor(wellIndex)
            wellIndex++
          }
          wellConcentrationData[well].push({
            timestamp,
            concentration: concentration.value,
          })
        })
      }
    })

    // Sort data by timestamp for each well
    Object.keys(wellConcentrationData).forEach(well => {
      wellConcentrationData[well].sort((a, b) => a.timestamp - b.timestamp)
    })

    // Get all timestamps for domain calculation
    const allTimestamps = Object.values(wellConcentrationData)
      .flatMap(data => data.map(obs => obs.timestamp))
      .sort()
    const minTime = allTimestamps[0] ?? Date.now() / 1000
    const maxTime = allTimestamps[allTimestamps.length - 1] ?? Date.now() / 1000

    // Create legend data with latest values
    const legendData = Object.entries(wellConcentrationData).map(([well, data]) => ({
      well,
      color: wellColors[well],
      latestValue: data[data.length - 1]?.concentration ?? 0,
    }))

    // Transform data to match D3LineGraphData format: { [key: string]: number }[]
    const transformedGraphData: { [well: string]: { [key: string]: number }[] } = {}
    Object.entries(wellConcentrationData).forEach(([well, observations]) => {
      transformedGraphData[well] = observations.map(obs => ({
        timestamp: obs.timestamp,
        concentration: obs.concentration,
      }))
    })

    return {
      graphData: transformedGraphData,
      legendData,
      allTimestamps,
      minTime,
      maxTime,
    }
  }, [plate.plateObservationHistory])

  // Return null if no cell concentration data is available
  if (Object.keys(graphData).length === 0) {
    return null
  }

  // Calculate dynamic Y-axis domain based on cell concentration values
  // 80th percentile is 1 billion cells/mL
  const allConcentrationValues = Object.values(graphData).flatMap(data =>
    data.map(obs => obs.concentration),
  )
  const minConcentration = Math.min(...allConcentrationValues)
  const maxConcentration = Math.max(...allConcentrationValues)

  // Add 10% buffer to the Y-axis range
  const range = maxConcentration - minConcentration
  const buffer = range * 0.1
  const yDomain: [number, number] = [
    Math.max(0, minConcentration - buffer),
    maxConcentration + buffer,
  ]

  // An empty region on either side of the X domain keeps the line from
  // overlapping the Y axis labels.
  const xAxisBuffer = ((maxTime - minTime) * 0.19) / 2
  const xDomain: [number, number] = [minTime - xAxisBuffer, maxTime + xAxisBuffer]
  const allDataOnSameDay =
    dayjs.unix(maxTime).format('YYMMDD') === dayjs.unix(minTime).format('YYMMDD')

  // Format cell concentration for display (e.g., 1.5B, 500M, 1.2K)
  return (
    <aside className='border-t-0.5 border-grey-light py-4 w-full h-full'>
      <h2 className='text-sm font-medium text-grey-darkest mb-4.5'>
        Cell Concentration Over Time
      </h2>

      <LineGraph
        className='h-[600px] w-full'
        layoutOptions={{
          marginTop: 0,
          marginRight: 0,
          marginLeft: 50,
        }}
        axisOptions={{
          xAxisBorderClassName: 'stroke-grey-medium',
          xTickValues:
            allTimestamps.length === 1
              ? [minTime]
              : [minTime, minTime + (maxTime - minTime) / 2, maxTime],
          xTickFormat:
            allDataOnSameDay && allTimestamps.length > 1
              ? (unix: number) => dayjs.unix(unix).format('h:mm A')
              : (unix: number) => dayjs.unix(unix).format('MMM D'),
          xTickSize: 0,
          xTickLabelClassName: 'text-xs font-bold translate-y-1',

          yAxisBorderClassName: 'hidden',
          yTickValues: (() => {
            const range = maxConcentration - minConcentration
            const step = range / 4
            return [
              0,
              minConcentration + step,
              minConcentration + 2 * step,
              minConcentration + 3 * step,
              maxConcentration,
            ]
          })(),
          yTickFormat: val => val.toExponential(2),
          yTickLineClassName: 'stroke-grey-lightest stroke-2 w-56 h-2',
          yTickLabelClassName:
            'text-xs font-bold text-start translate-x-0.5 translate-y-2.25 w-full',
          yTickSize: -5000,
        }}
        options={{
          dotSize: 0,
          xDomain,
          xKey: 'timestamp',
          yDomain,
          yKey: 'concentration',
          lineColor: (key: string) => {
            const well = key
            const wellData = legendData.find(item => item.well === well)
            return wellData?.color ?? '#666'
          },
          highlightedLine: highlightedWell,
        }}
        data={graphData}
      />
      {highlightedWell && (
        <div className='text-grey-dark font-subtext text-center font-bold'>
          Well {highlightedWell}
        </div>
      )}
    </aside>
  )
}
